// @flow

import React, { useEffect, useState, useRef } from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { TweenMax, Power2 } from 'gsap';
import { gsap, ScrollToPlugin, CSSPlugin } from 'gsap/all';
import { rem } from 'polished';
import { useDispatch } from 'react-redux';
import dompurify from 'dompurify';
import { getLang } from '../../../i18n/l10n';

import { media, mediaBreakpointsRaw, fontFamily } from '../../../styles/GlobalStyle';
import { UPDATE_TITLE } from '../../../store/actions';
import winamaxLogoImage from '../../../assets/images/w-logo.svg';
import { importAll } from '../../../utils/CommonUtils';
import { useScroll } from '../hooks/useScroll';
import { ImageLoader } from './ImageLoader';
import backgroundImage from '../../../assets/images/covers/background.png';

const covers = importAll(
  // TOTHINK: don't use @assets alias bescause Jest will fail
  // $FlowFixMe
  require.context('../../../assets/images/covers', false, /\.(png|jpe?g|svg)$/)
);

type LayerAnimationRawProps = {
  className?: string,
  step: number,
  title: string,
  paddingRightVw?: number,
  titleMobile?: string,
};

const LeftShadow = styled.div`
  position: absolute;
  width: 40%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1;

  background: linear-gradient(to right, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%);
`;

if (gsap) gsap.registerPlugin(ScrollToPlugin, CSSPlugin);

const LayerAnimationRaw = ({
  className,
  step,
  title,
  paddingRightVw,
  titleMobile = '',
}: LayerAnimationRawProps) => {
  const sanitizer = dompurify.sanitize;
  const [isSmallDevice, setSmallDevice] = useState(null);
  const layerAnimationRef = useRef();
  // const [transitionOver, setTransitionOver] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  // const [width, setWidth] = useState(window.innerWidth);
  const dispatch = useDispatch();
  const { y } = useScroll();

  useEffect(() => {
    if (window.innerWidth < mediaBreakpointsRaw.desktop) setSmallDevice(true);

    const vh = window.innerHeight * 0.01;
    // Then we set the value in the --vh custom property to the root of the document
    const de: any = document.documentElement;
    de.style.setProperty('--vh', `${vh}px`);

    // Update mobile title
    if (titleMobile) {
      dispatch({
        type: UPDATE_TITLE,
        payload: titleMobile,
      });
    }
  }, [dispatch, titleMobile]);

  useEffect(() => {
    const localIsSmallDevice = window.innerWidth < mediaBreakpointsRaw.desktop;
    const timeAnimIn = 0.3;
    const del = 2;
    const timeAnimOut = 2;

    TweenMax.to(window, 2, {
      scrollTo: { y: 0, autoKill: false },
      ease: Power2.easeInOut,
    });

    // Animation in
    TweenMax.fromTo(
      '.layerAnimation-gradient',
      timeAnimIn,
      { opacity: 0, y: '100vh' },
      { opacity: 1, y: 0, ease: Power2.easeOut }
    );
    TweenMax.fromTo('.left-side h1', timeAnimIn, { opacity: 0 }, { opacity: 1, delay: 0.4 });
    TweenMax.fromTo(
      '.layerAnimation-img',
      timeAnimIn,
      { opacity: 0, y: '100vh', scale: 1.3 },
      { opacity: 1, y: 30, scale: 1, ease: Power2.easeOut, delay: 0.3 }
    );

    // Animation out
    TweenMax.fromTo(
      'h1',
      !localIsSmallDevice ? 1 : 0.3,
      { scale: 1.2, transformOrigin: '0 100%' },
      { scale: 1, ease: Power2.easeOut, delay: !localIsSmallDevice ? del : 0 }
    );
    TweenMax.to('.layerAnimation-img', timeAnimOut, { y: 0, delay: del });

    if (localIsSmallDevice) {
      TweenMax.to('.layerAnimation-cont', 1, {
        y: '-100vh',
        autoAlpha: 0,
        ease: Power2.easeOut,
        delay: del,
      });
      TweenMax.set('.right-side', { y: '100vh' });
      TweenMax.to('.right-side', 1, {
        y: 0,
        autoAlpha: 1,
        ease: Power2.easeOut,
        delay: del,
      });
    } else {
      TweenMax.to('.layerAnimation-cont', timeAnimOut, {
        autoAlpha: 0,
        delay: del,
      });
    }

    if (!localIsSmallDevice) {
      TweenMax.fromTo(
        ['.buttonBack', '.right-side'],
        1,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          ease: Power2.easeOut,
          delay: del + timeAnimOut - 1,
        }
      );
    }
  }, []);

  const handleResize = () => {
    if (isSmallDevice && window.innerWidth >= mediaBreakpointsRaw.desktop) {
      TweenMax.to('.layerAnimation-cont', 1, { autoAlpha: 0 });
    }
  };

  // Detect if smallDevice to !smallDevice, remove fixed
  useEffect(() => {
    // const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  useEffect(() => {
    const leftSideContent = document.querySelector('.left-side-content');
    if (y > 200) {
      if (leftSideContent) {
        leftSideContent.classList.add('sticky');
        setHasScrolled(true);
      }
    }
  }, [y, hasScrolled]);

  // Cover
  const [coverImage, setCoverImage] = useState('');
  useEffect(() => {
    setCoverImage(covers[`step${step}.png`]);
  }, [step]);

  return (
    <div
      className={`${className || ''}${getLang() !== 'fr' ? ' no-disclaimer' : ''}`}
      ref={layerAnimationRef}
    >
      <div className="layerAnimation-cont">
        <div className="layerAnimation-logo">
          <img src={winamaxLogoImage} alt="Winamax" />
        </div>
        <h1 dangerouslySetInnerHTML={{ __html: sanitizer(title) }} />
        <ImageLoader
          img={coverImage}
          paddingRightVw={!isSmallDevice ? paddingRightVw : null}
          isMobile={isSmallDevice}
        />
        <LeftShadow />
        <div className="layerAnimation-gradient" />
      </div>
    </div>
  );
};

export const LayerAnimation: StyledComponent<
  LayerAnimationRawProps,
  {},
  typeof LayerAnimationRaw
> = styled(LayerAnimationRaw)`
  .layerAnimation-cont {
    width: 100vw;
    height: 100vh; /* Fallback for browsers that do not support Custom Properties */
    height: calc(var(--vh, 1vh) * 100);
    top: 0;
    left: 0;
    z-index: 3;
    display: flex;
    justify-content: flex-end;
    position: absolute;
    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      display: block;
      width: 100%;
      height: 70%;
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 1) 70%,
        rgba(0, 0, 0, 1) 100%
      );
      opacity: 1;
      z-index: 1;

      ${media.greaterThan('desktop')`
        background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 100%);
      `}
    }
    ${media.greaterThan('desktop')`
      width: 100%;
      position: fixed;
      height: 100%;
      top: 60px;
      height: calc(100% - 60px);
    `}
  }

  .layerAnimation-gradient {
    background: url(${backgroundImage});
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }

  .layerAnimation-logo {
    position: absolute;
    top: -15px;
    left: 20px;
    z-index: 2;
    padding: 30px 30px 0 0;
    ${media.greaterThan('desktop')`
      display: none;
    `}
  }

  h1 {
    position: absolute;
    bottom: 40px;
    left: 20px;
    z-index: 2;
    font-size: ${rem('40px')};
    font-family: ${fontFamily.primary.black.fontFamily};
    font-weight: ${fontFamily.primary.black.fontWeight};
    line-height: 42px;
    ${media.greaterThan('desktop')`
      display: none;
    `}
  }

  ${media.greaterThan('desktop')`
    &.no-disclaimer .layerAnimation-cont {
      top: 0;
      height: 100%;
    }
  `}
`;
